import APIRequest from "../API";
import * as PATH from "../API/path";
import { clearLocalCreatorPushSubscription } from "./pushNotificationLocal";

export type CreatorNotificationState =
  | "unsupported"
  | "blocked"
  | "disabled"
  | "enabled";

interface PushRegistrationResponse {
  status: boolean;
  message?: string;
  data?: {
    deviceToken?: string;
    publicKey?: string;
  };
}

interface PushPublicKeyResponse {
  status: boolean;
  message?: string;
  data?: {
    publicKey?: string;
  };
}

const apiBaseUrl = String(process.env.REACT_APP_API_ENDPOINT || "").replace(
  /\/+$/,
  ""
);
let cachedPublicKey = "";
let publicKeyRequest: Promise<string> | null = null;

export const supportsCreatorNotifications = (): boolean =>
  typeof window !== "undefined" &&
  window.isSecureContext &&
  "Notification" in window &&
  "serviceWorker" in navigator &&
  "PushManager" in window;

const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const normalized = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const rawData = window.atob(normalized);

  return Uint8Array.from(rawData, (character) => character.charCodeAt(0));
};

const applicationServerKeysMatch = (
  subscription: PushSubscription,
  expectedKey: Uint8Array
): boolean => {
  const currentKey = subscription.options.applicationServerKey;
  if (!currentKey) return false;

  const currentBytes = new Uint8Array(currentKey as ArrayBuffer);
  if (currentBytes.length !== expectedKey.length) return false;

  return currentBytes.every((value, index) => value === expectedKey[index]);
};

const getReadyRegistration = async (): Promise<ServiceWorkerRegistration> => {
  if (!supportsCreatorNotifications()) {
    throw new Error("Browser notifications are not supported on this device.");
  }

  return navigator.serviceWorker.ready;
};

const postConfigurationToWorker = (
  registration: ServiceWorkerRegistration,
  configuration: {
    deviceToken: string;
    publicKey: string;
  }
): void => {
  const worker =
    registration.active || registration.waiting || registration.installing;

  worker?.postMessage({
    type: "FANXO_SET_PUSH_CONFIG",
    deviceToken: configuration.deviceToken,
    apiBaseUrl,
    vapidPublicKey: configuration.publicKey,
  });
};

const clearWorkerConfiguration = async (): Promise<void> => {
  if (!("serviceWorker" in navigator)) return;

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const worker =
      registration?.active || registration?.waiting || registration?.installing;
    worker?.postMessage({ type: "FANXO_CLEAR_PUSH_CONFIG" });
  } catch {
    // Local notification cleanup should never block sign-out or account actions.
  }
};

const requestPublicKey = async (): Promise<string> => {
  const response: any = await APIRequest<PushPublicKeyResponse>(
    PATH.getPushPublicKey
  );

  if (response.error) {
    throw new Error(response.error.message);
  }

  const publicKey = response.data?.data?.publicKey;
  if (!response.data?.status || !publicKey) {
    throw new Error(
      response.data?.message ||
        "Creator notifications are not configured in this environment."
    );
  }

  cachedPublicKey = publicKey;
  return publicKey;
};

const getPublicKey = (): Promise<string> => {
  if (cachedPublicKey) return Promise.resolve(cachedPublicKey);
  if (!publicKeyRequest) {
    publicKeyRequest = requestPublicKey().finally(() => {
      publicKeyRequest = null;
    });
  }
  return publicKeyRequest;
};

export const prepareCreatorNotifications = async (): Promise<void> => {
  if (!supportsCreatorNotifications()) return;
  await Promise.all([getReadyRegistration(), getPublicKey()]);
};

const registerSubscriptionWithFanxo = async (
  subscription: PushSubscription,
  publicKey: string
): Promise<void> => {
  const response: any = await APIRequest<PushRegistrationResponse>(
    PATH.registerPushSubscription,
    { subscription: subscription.toJSON() }
  );

  if (response.error) {
    throw new Error(response.error.message);
  }

  const deviceToken = response.data?.data?.deviceToken;
  const resolvedPublicKey = response.data?.data?.publicKey || publicKey;

  if (!response.data?.status || !deviceToken) {
    throw new Error(
      response.data?.message || "Unable to register creator notifications."
    );
  }

  postConfigurationToWorker(await getReadyRegistration(), {
    deviceToken,
    publicKey: resolvedPublicKey,
  });
};

const subscribeOrSync = async (
  registration: ServiceWorkerRegistration,
  publicKey: string
): Promise<CreatorNotificationState> => {
  const applicationServerKey = urlBase64ToUint8Array(publicKey);
  let subscription = await registration.pushManager.getSubscription();

  if (subscription && !applicationServerKeysMatch(subscription, applicationServerKey)) {
    await subscription.unsubscribe();
    subscription = null;
  }

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey as BufferSource,
    });
  }

  await registerSubscriptionWithFanxo(subscription, publicKey);
  return "enabled";
};

export const getCreatorNotificationState = async (): Promise<CreatorNotificationState> => {
  if (!supportsCreatorNotifications()) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  if (Notification.permission !== "granted") return "disabled";

  const registration = await getReadyRegistration();
  const subscription = await registration.pushManager.getSubscription();
  return subscription ? "enabled" : "disabled";
};

export const enableCreatorNotifications = async (): Promise<CreatorNotificationState> => {
  if (!supportsCreatorNotifications()) return "unsupported";

  // Start both prerequisites directly from the creator's button gesture. This
  // keeps the eventual PushManager.subscribe call compatible with stricter PWA
  // browsers, including installed iOS Home Screen apps.
  const registrationPromise = getReadyRegistration();
  const publicKeyPromise = getPublicKey();

  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }

  if (permission === "denied") return "blocked";
  if (permission !== "granted") return "disabled";

  const [registration, publicKey] = await Promise.all([
    registrationPromise,
    publicKeyPromise,
  ]);
  return subscribeOrSync(registration, publicKey);
};

export const syncCreatorNotifications = async (): Promise<CreatorNotificationState> => {
  if (!supportsCreatorNotifications()) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  if (Notification.permission !== "granted") return "disabled";

  const [registration, publicKey] = await Promise.all([
    getReadyRegistration(),
    getPublicKey(),
  ]);
  return subscribeOrSync(registration, publicKey);
};

const removeSubscriptionFromFanxo = async (
  subscription: PushSubscription
): Promise<void> => {
  const response: any = await APIRequest(PATH.removePushSubscription, {
    endpoint: subscription.endpoint,
  });

  if (response.error) {
    throw new Error(
      response.error.message || "Unable to remove push subscription."
    );
  }
};

export const disableCreatorNotifications = async (): Promise<CreatorNotificationState> => {
  if (!supportsCreatorNotifications()) return "unsupported";

  const registration = await getReadyRegistration();
  const subscription = await registration.pushManager.getSubscription();

  if (subscription) {
    try {
      await removeSubscriptionFromFanxo(subscription);
    } finally {
      await subscription.unsubscribe();
    }
  }

  await clearWorkerConfiguration();
  return Notification.permission === "denied" ? "blocked" : "disabled";
};

export const clearCreatorNotificationsForSignOut = async (): Promise<void> => {
  if (!supportsCreatorNotifications()) {
    await clearLocalCreatorPushSubscription();
    return;
  }

  const registration = await getReadyRegistration();
  const subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    await clearWorkerConfiguration();
    return;
  }

  try {
    // Detach this endpoint from the signed-out account, but retain the browser
    // subscription so a later login can re-register it without asking again.
    await removeSubscriptionFromFanxo(subscription);
    await clearWorkerConfiguration();
  } catch (error) {
    // Fail closed if server detachment fails: a signed-out account must not
    // continue receiving notifications on this device.
    await clearLocalCreatorPushSubscription();
    throw error;
  }
};
